import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, BadgeCheck, HeartHandshake, Leaf, School, Soup, Trees, Wheat } from "lucide-react";
import { toast } from "sonner";
import { captureLeadTracking, getSavedTracking, trackAdClickEvent, trackLeadEvent } from "@/lib/leadTracking";
import {
  GIVES_BACK_STORAGE_KEY,
  givesBackCauses,
  givesBackFaqs,
  givesBackHero,
  givesBackIllustrativeExamples,
  givesBackLaunchStateMessage,
  givesBackPolicyNotes,
  givesBackProcessSteps,
} from "@/components/equinox/givesBackData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const causeIcons = {
  educate_child: School,
  feed_someone: Soup,
  feed_stray: HeartHandshake,
  care_for_cow: Wheat,
  plant_tree: Trees,
};

const impactPlaceholders = [
  { label: "Reporting period", value: "Awaiting first published cycle" },
  { label: "Total contributions allocated", value: "Will appear after verified updates" },
  { label: "Total contributions completed", value: "Will appear after verified updates" },
];

const readStoredUpdateToken = () => {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage.getItem(GIVES_BACK_STORAGE_KEY) || "";
  } catch (error) {
    console.warn("Gives Back storage unavailable", error);
    return "";
  }
};

const writeStoredUpdateToken = (value) => {
  if (typeof window === "undefined" || !value) return;

  try {
    window.localStorage.setItem(GIVES_BACK_STORAGE_KEY, value);
  } catch (error) {
    console.warn("Unable to store Gives Back update token", error);
  }
};

const updatePageMeta = () => {
  if (typeof document === "undefined") return () => {};

  const previousTitle = document.title;
  const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
  const previousCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") || "";

  document.title = "Equinox Gives Back | Equinox Marketing Agency";

  const setMeta = (selector, attr, value) => {
    let tag = document.head.querySelector(selector);
    if (!tag) {
      tag = document.createElement("meta");
      document.head.appendChild(tag);
    }
    tag.setAttribute(attr, value);
  };

  const setLink = (selector, rel, href) => {
    let tag = document.head.querySelector(selector);
    if (!tag) {
      tag = document.createElement("link");
      document.head.appendChild(tag);
    }
    tag.setAttribute("rel", rel);
    tag.setAttribute("href", href);
  };

  const canonicalUrl = typeof window !== "undefined" ? `${window.location.origin}/gives-back` : "/gives-back";
  const description =
    "Equinox Gives Back by Equinox Marketing Agency — 1% of eligible revenue from every invoice supports education, meals, stray feeding, cow care, and tree planting.";

  setMeta('meta[name="description"]', "name", "description");
  document.head.querySelector('meta[name="description"]')?.setAttribute("content", description);
  setMeta('meta[property="og:title"]', "property", "og:title");
  document.head.querySelector('meta[property="og:title"]')?.setAttribute("content", "Equinox Gives Back | Equinox Marketing Agency");
  setMeta('meta[property="og:description"]', "property", "og:description");
  document.head.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
  setMeta('meta[property="og:type"]', "property", "og:type");
  document.head.querySelector('meta[property="og:type"]')?.setAttribute("content", "website");
  setMeta('meta[property="og:url"]', "property", "og:url");
  document.head.querySelector('meta[property="og:url"]')?.setAttribute("content", canonicalUrl);
  setMeta('meta[name="twitter:card"]', "name", "twitter:card");
  document.head.querySelector('meta[name="twitter:card"]')?.setAttribute("content", "summary_large_image");
  setMeta('meta[name="twitter:title"]', "name", "twitter:title");
  document.head.querySelector('meta[name="twitter:title"]')?.setAttribute("content", "Equinox Gives Back | Equinox Marketing Agency");
  setMeta('meta[name="twitter:description"]', "name", "twitter:description");
  document.head.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
  setLink('link[rel="canonical"]', "canonical", canonicalUrl);

  return () => {
    document.title = previousTitle;
    document.head.querySelector('meta[name="description"]')?.setAttribute("content", previousDescription);
    if (previousCanonical) {
      document.head.querySelector('link[rel="canonical"]')?.setAttribute("href", previousCanonical);
    }
  };
};

export default function GivesBackPage() {
  const reduceMotion = useReducedMotion();
  const [selectedCause, setSelectedCause] = useState("");
  const [form, setForm] = useState({ name: "", company_name: "", email: "", message: "", honeypot: "" });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [impactSummary, setImpactSummary] = useState({ has_verified_updates: false, cause_wise_allocation: [], evidence_items: [], launch_state_message: givesBackLaunchStateMessage });
  const [existingSelection, setExistingSelection] = useState(null);
  const [submissionState, setSubmissionState] = useState(null);
  const pageLoadedAt = useRef(Date.now());

  useEffect(() => updatePageMeta(), []);

  useEffect(() => {
    const loadPublicImpact = async () => {
      try {
        const response = await axios.get(`${API}/gives-back/public-impact`);
        setImpactSummary(response.data);
      } catch (error) {
        console.warn("Unable to load Gives Back public impact summary", error);
      }
    };

    loadPublicImpact();
  }, []);

  useEffect(() => {
    const hydrateExistingSelection = async () => {
      const savedTracking = captureLeadTracking() || getSavedTracking() || {};
      const updateToken = readStoredUpdateToken();
      if (!updateToken && !savedTracking?.session_id) return;

      try {
        const response = await axios.get(`${API}/gives-back/selections/lookup`, {
          params: {
            update_token: updateToken,
            session_id: savedTracking?.session_id || "",
          },
        });

        if (response.data?.id) {
          setExistingSelection(response.data);
          setSelectedCause(response.data.cause);
          setForm({
            name: response.data.name || "",
            company_name: response.data.company_name || "",
            email: response.data.email || "",
            message: response.data.message || "",
            honeypot: "",
          });
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.warn("Unable to hydrate Gives Back selection", error);
        }
      }
    };

    hydrateExistingSelection();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash === "#cause-selection") {
      const section = document.getElementById("cause-selection");
      section?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }
  }, [reduceMotion]);

  const selectedCauseContent = useMemo(
    () => givesBackCauses.find((cause) => cause.id === selectedCause) || null,
    [selectedCause],
  );

  const scrollToId = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  const handleCauseSelect = (causeId, shouldScroll = false) => {
    setSelectedCause(causeId);
    setFormError("");
    trackAdClickEvent({ label: `gives_back_select_${causeId}`, metadata: { cause: causeId, placement: "gives_back_cause_cards" } });
    if (shouldScroll) {
      window.setTimeout(() => scrollToId("cause-selection"), 40);
    }
  };

  const submitSelection = async (event) => {
    event.preventDefault();

    if (!selectedCause) {
      setFormError("Please choose one cause before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError("");

      const tracking = captureLeadTracking() || getSavedTracking() || {};
      const payload = {
        cause: selectedCause,
        name: form.name,
        company_name: form.company_name,
        email: form.email,
        message: form.message,
        honeypot: form.honeypot,
        source: "gives_back_page",
        page_url: typeof window !== "undefined" ? window.location.href : "",
        page_path: "/gives-back",
        referrer: typeof document !== "undefined" ? document.referrer || "" : "",
        session_id: tracking?.session_id || "",
        engagement_reference: tracking?.session_id || "",
        update_token: readStoredUpdateToken(),
        submitted_after_ms: Date.now() - pageLoadedAt.current,
      };

      const response = await axios.post(`${API}/gives-back/selections`, payload);
      const savedSelection = response.data;

      if (savedSelection?.update_token) {
        writeStoredUpdateToken(savedSelection.update_token);
      }

      setExistingSelection(savedSelection);
      setSubmissionState(savedSelection);
      trackLeadEvent("lead_submit", {
        label: "gives_back_selection_submit",
        metadata: { cause: savedSelection.cause, submission_status: savedSelection.submission_status },
      });
      trackAdClickEvent({ label: "gives_back_submit", metadata: { cause: savedSelection.cause } });
      toast.success(savedSelection.submission_status === "updated" ? "Your cause preference has been updated." : "Your cause selection has been recorded.");
    } catch (error) {
      setFormError(error?.response?.data?.detail || "We couldn’t save your cause selection right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-28 lg:pt-20" data-testid="gives-back-page">
      <div className="mx-auto max-w-7xl space-y-10">
        <section className="noise-surface glitter-card glass-panel overflow-hidden rounded-[2.8rem] p-6 lg:p-8" data-testid="gives-back-hero-section">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="section-kicker">{givesBackHero.eyebrow}</p>
              <h1 className="mt-5 max-w-[11ch] font-display text-4xl tracking-[-0.06em] text-white sm:text-5xl lg:text-6xl" data-testid="gives-back-hero-heading">
                {givesBackHero.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-white/[0.66]" data-testid="gives-back-hero-copy">
                {givesBackHero.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => scrollToId("cause-selection")}
                  className="glass-button rounded-full bg-blue-500 px-7 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                  data-testid="gives-back-hero-primary-cta"
                >
                  <span className="relative z-10 inline-flex items-center gap-2">Choose a Cause <ArrowRight className="h-4 w-4" /></span>
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId("impact-reporting")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 transition-transform duration-300 hover:-translate-y-0.5 hover:text-white"
                  data-testid="gives-back-hero-secondary-cta"
                >
                  Explore Our Impact
                </button>
              </div>
              <div className="mt-7 grid gap-3 sm:grid-cols-3" data-testid="gives-back-trust-indicators">
                {givesBackHero.trustIndicators.map((item) => (
                  <div key={item} className="control-room-panel rounded-[1.3rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-amber-200">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-[2.2rem] border border-white/[0.08] bg-black/20 p-3">
              <img src={givesBackHero.heroImage} alt={givesBackHero.heroAlt} className="aspect-[5/6] w-full rounded-[1.8rem] object-cover" data-testid="gives-back-hero-image" />
              <div className="pointer-events-none absolute inset-3 rounded-[1.8rem] bg-[linear-gradient(180deg,rgba(2,6,23,0.04),rgba(2,6,23,0.55))]" />
            </div>
          </div>
        </section>

        <section className="space-y-5" data-testid="gives-back-cause-cards-section">
          <div className="max-w-3xl">
            <p className="section-kicker">Choose a cause</p>
            <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl lg:text-5xl">Five ways your growth can support something good.</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3" role="radiogroup" aria-label="Choose a cause" data-testid="gives-back-cause-radiogroup">
            {givesBackCauses.map((cause, index) => {
              const Icon = causeIcons[cause.id] || Leaf;
              const selected = selectedCause === cause.id;

              return (
                <motion.article
                  key={cause.id}
                  initial={reduceMotion ? false : { opacity: 0, y: 22 }}
                  whileInView={reduceMotion ? {} : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: index * 0.05, ease: "easeOut" }}
                  className={`noise-surface glitter-card cinematic-card overflow-hidden rounded-[2rem] border p-3 ${selected ? "border-amber-300/45 bg-[linear-gradient(180deg,rgba(245,158,11,0.08),rgba(255,255,255,0.02))]" : "border-white/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.018))]"}`}
                  data-testid={`gives-back-cause-card-${cause.id}`}
                >
                  <div className="relative overflow-hidden rounded-[1.6rem] border border-white/[0.08]">
                    <img src={cause.image} alt={cause.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid={`gives-back-cause-image-${cause.id}`} />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,4,8,0),rgba(3,4,8,0.68))]" />
                    <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/[0.1] bg-[#050811]/65 px-3 py-2 text-xs uppercase tracking-[0.2em] text-white/82 backdrop-blur-xl">
                      <Icon className="h-4 w-4 text-amber-200" />
                      {cause.emoji}
                    </div>
                  </div>
                  <div className="px-2 pb-3 pt-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-2xl text-white">{cause.title}</p>
                        <p className="mt-3 text-sm leading-7 text-white/[0.64]">{cause.description}</p>
                      </div>
                      {selected ? <BadgeCheck className="mt-1 h-5 w-5 text-amber-200" data-testid={`gives-back-cause-selected-icon-${cause.id}`} /> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCauseSelect(cause.id, true)}
                      className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-transform duration-300 hover:-translate-y-0.5 ${selected ? "border border-amber-200/35 bg-amber-300/12 text-amber-100" : "border border-white/[0.12] bg-white/[0.04] text-white/84 hover:border-blue-300/30 hover:bg-blue-400/10"}`}
                      data-testid={`gives-back-cause-button-${cause.id}`}
                      aria-pressed={selected}
                      role="radio"
                      aria-checked={selected}
                    >
                      {selected ? "Selected Cause" : "Choose This Cause"}
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>

        <section id="cause-selection" className="noise-surface glitter-card glass-panel rounded-[2.6rem] p-6 lg:p-8" data-testid="gives-back-form-section">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="section-kicker">Choose the change you want to see</p>
              <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">Every invoice makes a difference.</h2>
              <p className="mt-5 max-w-xl text-sm leading-8 text-white/[0.64]">
                Tell us which cause you would like your contribution to support.
              </p>
              <div className="mt-6 rounded-[1.6rem] border border-white/[0.08] bg-black/20 p-5" data-testid="gives-back-selected-cause-panel">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">Selected cause</p>
                <p className="mt-3 font-display text-2xl text-white">{selectedCauseContent ? `${selectedCauseContent.title} ${selectedCauseContent.emoji}` : "Choose one of the five causes above"}</p>
                <p className="mt-3 text-sm leading-7 text-white/[0.62]">
                  {selectedCauseContent ? selectedCauseContent.description : "Your selection guides the contribution associated with your engagement when a cause preference is recorded."}
                </p>
                {existingSelection ? (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-blue-300/25 bg-blue-400/10 px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-blue-100" data-testid="gives-back-existing-selection-note">
                    Existing selection found — you can update it below.
                  </div>
                ) : null}
              </div>
            </div>

            <form onSubmit={submitSelection} className="space-y-4" data-testid="gives-back-selection-form">
              <input
                type="text"
                value={form.honeypot}
                onChange={(event) => setForm((current) => ({ ...current, honeypot: event.target.value }))}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <label className="block text-sm text-white/[0.78]">
                <span className="mb-3 block">Cause selection *</span>
                <div className="rounded-[1.35rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-white" data-testid="gives-back-selected-cause-input">
                  {selectedCauseContent ? `${selectedCauseContent.title} ${selectedCauseContent.emoji}` : "Select one cause above"}
                </div>
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block text-sm text-white/[0.78]">
                  <span>Name</span>
                  <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40" data-testid="gives-back-name-input" />
                </label>
                <label className="block text-sm text-white/[0.78]">
                  <span>Company name</span>
                  <input value={form.company_name} onChange={(event) => setForm((current) => ({ ...current, company_name: event.target.value }))} className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40" data-testid="gives-back-company-input" />
                </label>
              </div>

              <label className="block text-sm text-white/[0.78]">
                <span>Email address</span>
                <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40" data-testid="gives-back-email-input" />
              </label>

              <label className="block text-sm text-white/[0.78]">
                <span>Message</span>
                <textarea value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} rows={5} className="mt-3 w-full rounded-[1.4rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-white outline-none transition-colors focus:border-blue-300/40" data-testid="gives-back-message-input" />
              </label>

              <div className="rounded-[1.4rem] border border-white/[0.08] bg-white/[0.04] p-4 text-sm leading-7 text-white/[0.66]" data-testid="gives-back-disclosure-copy">
                Equinox Marketing Agency contributes 1% of eligible revenue from every invoice to its Gives Back initiative. Your cause selection helps us direct the contribution associated with your engagement. No additional donation is required from you.
              </div>

              {formError ? <div className="rounded-[1.2rem] border border-rose-300/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" data-testid="gives-back-form-error" aria-live="polite">{formError}</div> : null}
              {submissionState ? (
                <div className="rounded-[1.2rem] border border-emerald-300/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100" data-testid="gives-back-form-success" aria-live="polite">
                  {submissionState.submission_status === "updated" ? "Your cause preference has been updated." : "Your cause preference has been recorded."}
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button type="submit" disabled={submitting} className="glass-button rounded-full bg-blue-500 px-7 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60" data-testid="gives-back-submit-button">
                  <span className="relative z-10 inline-flex items-center gap-2">{submitting ? "Saving..." : "Submit My Cause Selection"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackAdClickEvent({ label: "gives_back_explore_impact", metadata: { placement: "gives_back_form" } });
                    scrollToId("impact-reporting");
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-6 py-4 text-sm font-medium text-white/82 transition-transform duration-300 hover:-translate-y-0.5 hover:text-white"
                  data-testid="gives-back-explore-impact-button"
                >
                  Explore Our Impact
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="noise-surface glitter-card glass-panel rounded-[2.6rem] p-6 lg:p-8" data-testid="gives-back-process-section">
          <p className="section-kicker">From Invoice to Impact</p>
          <h2 className="mt-5 max-w-[11ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">How our commitment works.</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-4">
            {givesBackProcessSteps.map((step, index) => (
              <div key={step.title} className="control-room-panel rounded-[1.5rem] p-5" data-testid={`gives-back-process-step-${index + 1}`}>
                <p className="text-[11px] uppercase tracking-[0.22em] text-amber-200">Step {index + 1}</p>
                <p className="mt-3 font-display text-2xl text-white">{step.title}</p>
                <p className="mt-4 text-sm leading-7 text-white/[0.64]">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {givesBackPolicyNotes.map((note, index) => (
              <div key={note} className="rounded-[1.25rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-sm leading-7 text-white/[0.66]" data-testid={`gives-back-policy-note-${index + 1}`}>
                {note}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-5" data-testid="gives-back-illustrative-section">
          <div className="max-w-3xl">
            <p className="section-kicker">Small steps. Brighter tomorrows.</p>
            <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">Illustrative examples of what smaller allocations may support.</h2>
            <p className="mt-5 text-sm uppercase tracking-[0.22em] text-amber-200">Illustrative examples. Actual costs and outcomes vary by location and availability.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {givesBackIllustrativeExamples.map((item, index) => (
              <div key={item.amount} className="noise-surface glitter-card glass-panel rounded-[1.8rem] p-5" data-testid={`gives-back-example-card-${index + 1}`}>
                <p className="font-display text-4xl tracking-[-0.05em] text-white">{item.amount}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-amber-200">{item.title}</p>
                <p className="mt-4 text-sm leading-7 text-white/[0.64]">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="impact-reporting" className="noise-surface glitter-card glass-panel rounded-[2.6rem] p-6 lg:p-8" data-testid="gives-back-transparency-section">
          <p className="section-kicker">Every contribution deserves transparency</p>
          <h2 className="mt-5 max-w-[11ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">Impact reporting that stays honest from day one.</h2>
          {!impactSummary.has_verified_updates ? (
            <div className="mt-8 rounded-[1.8rem] border border-amber-300/18 bg-amber-300/10 p-5 text-sm leading-7 text-amber-50" data-testid="gives-back-launch-state-message">
              {impactSummary.launch_state_message || givesBackLaunchStateMessage}
            </div>
          ) : null}
          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {(impactSummary.has_verified_updates ? [
              { label: "Reporting period", value: impactSummary.reporting_period || "Verified cycle" },
              { label: "Total contributions allocated", value: impactSummary.total_contributions_allocated || "Available" },
              { label: "Total contributions completed", value: impactSummary.total_contributions_completed || "Available" },
            ] : impactPlaceholders).map((item, index) => (
              <div key={item.label} className="control-room-panel rounded-[1.5rem] p-5" data-testid={`gives-back-impact-card-${index + 1}`}>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">{item.label}</p>
                <p className="mt-3 font-display text-2xl text-white">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-[1.8rem] border border-white/[0.08] bg-black/20 p-5" data-testid="gives-back-cause-wise-allocation-panel">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">Cause-wise allocation</p>
              <div className="mt-4 space-y-3 text-sm text-white/[0.68]">
                {impactSummary.has_verified_updates && impactSummary.cause_wise_allocation?.length ? impactSummary.cause_wise_allocation.map((entry, index) => (
                  <div key={`${entry.cause}-${index}`} className="rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <span>{entry.label}</span>
                      <span className="text-white/84">{entry.value}</span>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] px-4 py-4">
                    Verified cause-wise allocation data will appear here once published.
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-[1.8rem] border border-white/[0.08] bg-black/20 p-5" data-testid="gives-back-evidence-panel">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">Photographs, receipts, and supporting evidence</p>
              <div className="mt-4 space-y-3 text-sm text-white/[0.68]">
                {impactSummary.has_verified_updates && impactSummary.evidence_items?.length ? impactSummary.evidence_items.map((item, index) => (
                  <div key={`${item}-${index}`} className="rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] px-4 py-4">
                    {item}
                  </div>
                )) : (
                  <div className="rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] px-4 py-4">
                    Evidence summaries will be published here only when verified updates are available.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="noise-surface glitter-card glass-panel rounded-[2.6rem] p-6 lg:p-8" data-testid="gives-back-faq-section">
          <p className="section-kicker">Frequently Asked Questions</p>
          <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">The practical questions clients usually ask.</h2>
          <Accordion type="single" collapsible className="mt-8">
            {givesBackFaqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`faq-${index + 1}`} className="border-white/[0.08]">
                <AccordionTrigger className="text-left text-base text-white hover:no-underline" data-testid={`gives-back-faq-trigger-${index + 1}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-7 text-white/[0.64]" data-testid={`gives-back-faq-content-${index + 1}`}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="noise-surface glitter-card glass-panel rounded-[2.6rem] p-6 lg:p-8" data-testid="gives-back-closing-cta-section">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="section-kicker">Closing note</p>
              <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">Let’s Make Growth Mean More.</h2>
              <p className="mt-5 max-w-2xl text-sm leading-8 text-white/[0.64]">
                Every business has the opportunity to create a positive impact. At Equinox Marketing Agency, we’re committed to making growth meaningful beyond business.
              </p>
            </div>
            <button
              type="button"
              onClick={() => scrollToId("cause-selection")}
              className="glass-button rounded-full bg-blue-500 px-7 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
              data-testid="gives-back-closing-cta-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">Choose Your Cause <ArrowRight className="h-4 w-4" /></span>
            </button>
          </div>
        </section>
      </div>
    </section>
  );
}